import { useQuery } from '@tanstack/react-query';

import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { propertyLine } from './labels';
import { type ObjectChoice, type OwnerObject, objectChoiceLabels, otherObjects } from './objectOutcomes';

// OwnerObjectOutcomes: Nachbearbeitung – die weiteren Leads des Eigentümers und der Personen im Haushalt (B-20), im
// selben Gespräch besprochen. Je Lead ein Ergebnis (Vorgabe „Nicht besprochen“); es wird mit dem Ergebnis des Anrufs
// gespeichert. „Termin eintragen …“ öffnet das Terminformular für diesen Lead. „Kein Interesse für alle Objekte“ erledigt
// alles mit einem Klick.
export function OwnerObjectOutcomes({
  leadId,
  choices,
  onChange,
  onAppointment,
  onNotInterestedAll,
  busy,
}: {
  leadId: string;
  choices: Record<string, ObjectChoice>;
  onChange: (leadId: string, choice: ObjectChoice) => void;
  onAppointment: (object: OwnerObject) => void;
  onNotInterestedAll: (objects: OwnerObject[]) => void;
  busy: boolean;
}) {
  const { project } = useProject();
  const papi = useProjectApi();
  const owners = useQuery({ queryKey: projectKeys.leadOwners(project.id, leadId), queryFn: ({ signal }) => papi.leadOwners(leadId, signal) });
  const objects = otherObjects(owners.data?.items ?? [], leadId, owners.data?.household);
  if (objects.length === 0) return null;
  const names = [...new Set(objects.map((o) => o.ownerName))];
  return (
    <section className="owner-objects" aria-label="Weitere Objekte des Eigentümers">
      <h3>
        {objects.length === 1 ? 'Weiteres Objekt' : `${objects.length} weitere Objekte`}
        {names.length === 1 ? ` von ${names[0]}` : ' im Haushalt'} – im Gespräch besprochen?
      </h3>
      <ul className="plain-list">
        {objects.map((o) => {
          const choice = choices[o.id] ?? '';
          return (
            <li key={o.id} className="owner-object">
              <span>
                <strong>{propertyLine(o)}</strong> <span className="badge badge-muted">{o.status_label}</span>
                {(names.length > 1 || o.household) && (
                  <span className="muted">
                    {' '}
                    · {o.ownerName}
                    {o.household && ' (Haushalt)'}
                  </span>
                )}
                {o.field_sales_name && <span className="muted"> · {o.field_sales_name}</span>}
              </span>
              {choice === 'APPOINTMENT_DONE' ? (
                <span className="badge badge-ok">Termin eingetragen</span>
              ) : (
                <select
                  aria-label={`Ergebnis für ${propertyLine(o)}`}
                  value={choice}
                  onChange={(e) => {
                    const c = e.target.value as ObjectChoice;
                    if (c === 'APPOINTMENT') onAppointment(o);
                    else onChange(o.id, c);
                  }}
                >
                  {Object.entries(objectChoiceLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              )}
            </li>
          );
        })}
      </ul>
      <p className="muted small">Gilt nur, wenn der Eigentümer erreicht wurde; gespeichert wird mit dem Gesprächsergebnis.</p>
      <button type="button" className="btn" disabled={busy} onClick={() => onNotInterestedAll(objects)}>
        Kein Interesse für alle Objekte
      </button>
    </section>
  );
}
